import { z } from 'zod';
import { RelationTypeSchema } from '../enums';

// Base Guest Donor Schema
export const BaseGuestDonorSchema = z.object({
  guestDonorId: z.string().optional(), // MongoDB ObjectId
  guestDonorName: z.string().min(1, 'Guest donor name is required').max(100, 'Name too long'),
  fatherOrHusbandName: z.string().min(1, 'Father/Husband name is required').max(100, 'Name too long'),
  relation: RelationTypeSchema,
  phoneNo: z.string().min(10, 'Phone number must be at least 10 digits').max(15, 'Phone number too long'),
  email: z.string().email('Invalid email format').optional(),
  officeNo: z.number().int().positive('Office number must be positive'),
  isConverted: z.boolean().default(false), // Converted to permanent donor
  convertedToDonorId: z.string().optional(), // Reference to permanent DonorID after conversion
  isActive: z.boolean().default(true),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional()
});

export type BaseGuestDonor = z.infer<typeof BaseGuestDonorSchema>;

// Guest Donor Creation Schema
export const CreateGuestDonorSchema = BaseGuestDonorSchema.omit({
  guestDonorId: true,
  isConverted: true,
  convertedToDonorId: true,
  createdAt: true,
  updatedAt: true
});

export type CreateGuestDonor = z.infer<typeof CreateGuestDonorSchema>;

// Guest Donor Update Schema
export const UpdateGuestDonorSchema = BaseGuestDonorSchema.partial().extend({
  guestDonorId: z.string() // Required for updates
});

export type UpdateGuestDonor = z.infer<typeof UpdateGuestDonorSchema>;

// Guest Donor Response Schema (for API responses)
export const GuestDonorResponseSchema = BaseGuestDonorSchema.extend({
  guestDonorId: z.string()
});

export type GuestDonorResponse = z.infer<typeof GuestDonorResponseSchema>;

// Guest Donor with Relations Schema
export const GuestDonorWithRelationsSchema = GuestDonorResponseSchema.extend({
  office: z.object({
    officeNo: z.number(),
    officeName: z.string(),
    regionId: z.number(),
    regionName: z.string()
  }).optional(),
  convertedDonor: z.object({
    donorId: z.string(),
    donorName: z.string(),
    cnic: z.string()
  }).optional(),
  totalDonations: z.number().int().min(0).optional(),
  totalAmount: z.number().min(0).optional(),
  lastDonationDate: z.date().optional()
});

export type GuestDonorWithRelations = z.infer<typeof GuestDonorWithRelationsSchema>;

// Guest Donor List Query Schema
export const GuestDonorListQuerySchema = z.object({
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
  search: z.string().optional(),
  officeNo: z.number().int().positive().optional(),
  regionId: z.number().int().positive().optional(),
  relation: RelationTypeSchema.optional(),
  isConverted: z.boolean().optional(),
  isActive: z.boolean().optional(),
  hasDonations: z.boolean().optional(),
  sortBy: z.enum(['guestDonorName', 'phoneNo', 'createdAt', 'lastDonationDate']).default('guestDonorName'),
  sortOrder: z.enum(['asc', 'desc']).default('asc')
});

export type GuestDonorListQuery = z.infer<typeof GuestDonorListQuerySchema>;

// Guest Donor Conversion Schema
export const GuestDonorConversionSchema = z.object({
  guestDonorId: z.string(),
  donorData: z.object({
    donorName: z.string().min(1, 'Donor name is required'),
    fatherOrHusbandName: z.string().min(1, 'Father/Husband name is required'),
    relation: RelationTypeSchema,
    cnic: z.string().length(13, 'CNIC must be exactly 13 digits'),
    phoneNo: z.string().min(10, 'Phone number must be at least 10 digits'),
    email: z.string().email('Invalid email format').optional(),
    referral: z.object({
      type: z.enum(['self', 'family']),
      referredBy: z.string().optional(),
      relationToReferrer: z.string().optional()
    }).optional()
  })
});

export type GuestDonorConversion = z.infer<typeof GuestDonorConversionSchema>;

// Guest Donor Statistics Schema
export const GuestDonorStatisticsSchema = z.object({
  guestDonorId: z.string(),
  guestDonorName: z.string(),
  officeNo: z.number(),
  officeName: z.string(),
  totalDonations: z.number(),
  totalAmount: z.number(),
  donationTypes: z.array(z.object({
    type: z.string(),
    count: z.number(),
    amount: z.number()
  })),
  isConverted: z.boolean(),
  period: z.object({
    start: z.date(),
    end: z.date()
  })
});

export type GuestDonorStatistics = z.infer<typeof GuestDonorStatisticsSchema>;

// Guest Donor Search Schema
export const GuestDonorSearchSchema = z.object({
  query: z.string().min(1, 'Search query is required'),
  officeNo: z.number().int().positive().optional(),
  limit: z.number().int().min(1).max(50).default(10)
});

export type GuestDonorSearch = z.infer<typeof GuestDonorSearchSchema>; 